import { Link } from 'react-router-dom';
import { Play, ChevronRight, Star, Heart, Brain, Flame, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import VoiceWave from '@/components/VoiceWave';

const features = [
  {
    icon: Brain,
    title: 'Rewire Your Mind',
    description: 'Transform limiting beliefs into empowering thoughts through science-backed affirmations.',
  },
  {
    icon: Heart,
    title: 'Build Self-Love',
    description: 'Cultivate unshakable confidence and deep self-acceptance.',
  },
  {
    icon: Flame,
    title: 'Ignite Your Potential',
    description: 'Unlock your hidden abilities and achieve extraordinary results.',
  },
  {
    icon: Moon,
    title: 'Sleep Better',
    description: 'Drift into peaceful, restorative sleep with calming meditations.',
  },
];

const testimonials = [
  {
    name: 'Sarah M.',
    role: 'Entrepreneur',
    image: 'https://images.unsplash.com/photo-1672622933740-52331291f511?w=200',
    quote: 'TuneAwaken completely changed how I start my mornings. The confidence I\'ve built in just 30 days is incredible.',
  },
  {
    name: 'David K.',
    role: 'Software Engineer',
    image: 'https://images.unsplash.com/photo-1769291396641-9e67701db131?w=200',
    quote: 'I was skeptical at first, but the sleep meditations have helped me overcome years of insomnia. Life-changing.',
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Hero Section */}
      <section 
        className="hero-bg relative min-h-screen flex flex-col justify-center"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1731779456484-cc0f6d8c789d?w=1920)`,
        }}
        data-testid="hero-section"
      >
        <div className="container mx-auto px-6 py-24 md:py-32 text-center">
          {/* Procedural voice-wave */}
          <div
            className="flex justify-center -mb-6 md:mb-8 -mt-10 md:mt-0 animate-fade-in"
            style={{ animationDelay: '0s', opacity: 0, animationFillMode: 'forwards' }}
          >
            <VoiceWave size="lg" speed={0.9} lineCount={6} className="max-w-full" />
          </div>

          {/* Main Headline */}
          <h1 
            className="text-5xl md:text-7xl lg:text-8xl font-serif font-light text-white mb-6 leading-tight animate-fade-in stagger-1"
            style={{ animationDelay: '0.1s', opacity: 0 }}
          >
            Awaken the Life{' '}
            <span className="gradient-text-violet">You Deserve.</span>
          </h1>

          {/* Subheadline */}
          <p 
            className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 animate-fade-in stagger-2"
            style={{ animationDelay: '0.2s', opacity: 0 }}
          >
            Tune into yourself. Transform from within.
          </p>

          {/* CTA Buttons */}
          <div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in stagger-3"
            style={{ animationDelay: '0.3s', opacity: 0 }}
          >
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-white text-zinc-900 hover:bg-zinc-200 rounded-full px-8 py-6 text-lg font-medium"
                data-testid="hero-cta-start"
              >
                <Play className="w-5 h-5 mr-2" fill="currentColor" />
                Begin Your Transformation
              </Button>
            </Link>
            <Link to="/library">
              <Button 
                variant="outline" 
                size="lg"
                className="bg-white/5 text-white hover:bg-white/10 border-white/10 rounded-full px-8 py-6 text-lg"
                data-testid="hero-cta-explore"
              >
                Explore Library
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
            <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
              <div className="w-1 h-2 bg-white/40 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32 bg-zinc-950 relative" data-testid="how-it-works-section">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <p className="text-sm tracking-widest text-amber-400 uppercase mb-4">How it works</p>
            <h2 className="text-4xl md:text-6xl font-serif text-white leading-tight">
              From sign-up to <span className="gradient-text-violet">inner shift</span>
            </h2>
            <p className="text-base md:text-lg text-zinc-400 mt-5 max-w-2xl mx-auto">
              Six small steps to rewire your inner story — backed by neuroscience, designed to feel effortless.
            </p>
          </div>

          {/* Steps grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[
              {
                n: '01',
                title: 'Sign Up',
                desc: 'Create a free account with email or Google sign-in. No credit card needed.',
                accent: 'amber',
              },
              {
                n: '02',
                title: 'Choose Your Path',
                desc: 'Start with the free 7-day challenge and complete it.',
                accent: 'amber',
              },
              {
                n: '03',
                title: 'Practice Daily',
                desc: 'Explore more tracks that sync with your daily affirmation.',
                accent: 'amber',
              },
              {
                n: '04',
                title: 'Join 14-Day Challenge',
                desc: 'Start winning again with the 14-day journey challenge.',
                accent: 'violet',
              },
              {
                n: '05',
                title: 'Track Your Progress',
                desc: 'See gratitude messages, streak counts, heatmaps, milestones, and badges.',
                accent: 'violet',
              },
              {
                n: '06',
                title: 'Grow Together',
                desc: 'Join groups, share resources, view leaderboards, and practice collectively.',
                accent: 'violet',
              },
            ].map((step, i) => (
              <div
                key={step.n}
                className="group relative rounded-2xl bg-zinc-900/40 border border-white/5 hover:border-amber-500/30 transition-colors p-6 md:p-7 animate-fade-in"
                style={{ animationDelay: `${i * 0.07}s`, opacity: 0, animationFillMode: 'forwards' }}
                data-testid={`how-step-${step.n}`}
              >
                {/* Step number */}
                <div className="flex items-baseline justify-between mb-4">
                  <span
                    className={`text-5xl md:text-6xl font-serif leading-none ${
                      step.accent === 'amber' ? 'text-amber-400/30' : 'text-violet-400/30'
                    } group-hover:opacity-90 transition-opacity`}
                  >
                    {step.n}
                  </span>
                  <span
                    className={`w-2.5 h-2.5 rounded-full ${
                      step.accent === 'amber' ? 'bg-amber-400' : 'bg-violet-400'
                    } shadow-[0_0_12px_currentColor]`}
                  />
                </div>

                <h3 className="text-xl md:text-2xl font-serif text-white mb-2">{step.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 text-center">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-zinc-100 font-semibold px-7 py-3.5 rounded-full transition-colors"
              data-testid="how-cta-btn"
            >
              Begin your journey
              <ChevronRight className="w-4 h-4" />
            </Link>
            <p className="text-xs text-zinc-600 mt-3">Free 7-day challenge · No card required</p>
          </div>
        </div>
      </section>

      {/* Problem Statement - The Science */}
      <section className="py-24 md:py-32 bg-zinc-950 relative noise" data-testid="problem-section">
        <div className="container mx-auto px-6">
          {/* Main headline */}
          <div className="max-w-4xl mx-auto text-center mb-12">
            <p className="text-sm tracking-widest text-violet-400 uppercase mb-6">The Problem</p>
            <h2 className="text-4xl md:text-6xl font-serif text-white mb-6 leading-tight">
              You Have <span className="gradient-text-violet">60,000</span> Thoughts a Day.
            </h2>
            <p className="text-2xl md:text-3xl font-serif text-zinc-400 mb-8">
              Most of Them Are Working Against You.
            </p>
          </div>

          {/* Negative thought examples */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-16">
            {[
              '"I\'m not good enough."',
              '"Who am I to want that?"',
              '"What if I fail?"',
            ].map((belief, index) => (
              <div 
                key={index}
                className="bg-zinc-900/50 border border-red-500/20 rounded-2xl p-6 text-center animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
              >
                <p className="text-zinc-300 italic text-xl">{belief}</p>
              </div>
            ))}
          </div>

          {/* Explanation */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-zinc-900/30 border border-white/5 rounded-3xl p-8 md:p-12">
              <p className="text-lg md:text-xl text-zinc-300 leading-relaxed mb-6">
                These aren't truths. They're <span className="text-white font-medium">patterns</span> — neural pathways that were wired into your brain through years of repetition.
              </p>
              <p className="text-lg md:text-xl text-zinc-400 leading-relaxed mb-6">
                Every time you think a negative thought, you strengthen it. It becomes automatic. Effortless. <span className="text-red-400">Default.</span>
              </p>
              <div className="border-l-4 border-violet-500 pl-6 mt-8">
                <p className="text-xl md:text-2xl text-white font-serif leading-relaxed">
                  But here's what neuroscience tells us: if these patterns were wired in through repetition, they can be <span className="gradient-text-violet">rewired out</span> the same way.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 md:py-32 bg-zinc-900/30" data-testid="features-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm tracking-widest text-teal-400 uppercase mb-4">The Solution</p>
            <h2 className="text-4xl md:text-5xl font-serif text-white">
              Reprogram Your Mind
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index}
                  className="group bg-zinc-900/50 backdrop-blur-sm border border-white/5 rounded-2xl p-6 hover:border-violet-500/30 transition-colors duration-300 animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                  data-testid={`feature-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-violet-800 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-zinc-500">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      {/* Testimonials */}
      <section className="py-24 md:py-32 bg-zinc-900/20" data-testid="testimonials-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm tracking-widest text-amber-400 uppercase mb-4">Real Stories</p>
            <h2 className="text-4xl md:text-5xl font-serif text-white">
              Transformations Happen Daily
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="testimonial-card rounded-2xl p-8 border border-white/5 animate-fade-in"
                style={{ animationDelay: `${index * 0.15}s`, opacity: 0 }}
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-violet-500/30"
                  />
                  <div>
                    <p className="text-white font-medium">{testimonial.name}</p>
                    <p className="text-sm text-zinc-500">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-zinc-300 leading-relaxed italic">"{testimonial.quote}"</p>
                <div className="flex gap-1 mt-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400" fill="currentColor" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-zinc-900/50 to-zinc-950" data-testid="cta-section">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-6xl font-serif text-white mb-6">
            You have one life.<br />
            One mind. One chance.
          </h2>
          <p className="text-xl text-zinc-400 mb-10 max-w-xl mx-auto">
            The voice in your head will either lift you up or hold you down. You get to choose which one.
          </p>
          <Link to="/dashboard">
            <Button 
              size="lg" 
              className="bg-white text-zinc-900 hover:bg-zinc-200 rounded-full px-10 py-6 text-lg font-medium animate-glow"
              data-testid="final-cta-btn"
            >
              Start Your Transformation — Free
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/5">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
                <span className="font-serif text-lg text-white">I</span>
              </div>
              <span className="font-serif text-lg text-zinc-400">Lifetunes.live</span>
            </div>
            <p className="text-sm text-zinc-600">
              Your Mind, Your Choice. © 2025 Lifetunes.live
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
